/**
 * Reports API — calls the /reports endpoints and returns typed data.
 * All functions require a valid chambers JWT.
 */
import { api } from './api';

// ─── Hearing Readiness ──────────────────────────────────────

export interface HearingReadinessItem {
  id: string;
  hearingDate: string;
  hearingType: string;
  status: string;
  isVirtual: boolean;
  videoLink: string | null;
  courtroom: string | null;
  court: { shortName: string } | null;
  caseId: string;
  caseNumber: string;
  caseName: string;
  leadBarrister: { firstName: string; lastName: string; memberType: string } | null;
  flags: ('no_bundle' | 'overdue_tasks' | 'pending_conference' | 'no_video_link')[];
  readinessScore: number;
}

export async function fetchHearingReadiness(days = 7): Promise<HearingReadinessItem[]> {
  const res = await api.get('/reports/hearing-readiness', { params: { days } });
  return (res.data as any).data ?? [];
}

// ─── Barrister Workload ─────────────────────────────────────

export interface BarristerWorkloadItem {
  barristerId: string;
  userId: string;
  firstName: string;
  lastName: string;
  memberType: string;
  activeMatters: number;
  hearings7d: number;
  hearings30d: number;
  workloadStatus: 'overloaded' | 'high' | 'normal' | 'light';
}

export async function fetchBarristerWorkload(): Promise<BarristerWorkloadItem[]> {
  const res = await api.get('/reports/barrister-workload');
  return (res.data as any).data ?? [];
}

// ─── Hearing Workload Trend (authoritative, access-scoped) ──

export interface HearingWorkloadMonth {
  month: string; // YYYY-MM
  completed: number;
  scheduled: number;
  adjourned: number;
}

export async function fetchHearingWorkloadTrend(months = 12): Promise<HearingWorkloadMonth[]> {
  const res = await api.get('/reports/hearing-workload-trend', { params: { months } });
  return (res.data as any).data ?? [];
}

// ─── Matter Health ──────────────────────────────────────────

export interface MatterHealthItem {
  caseId: string;
  caseNumber: string;
  caseName: string;
  status: string;
  daysSinceUpdate: number;
  practiceArea: string | null;
  leadBarrister: { firstName: string; lastName: string; memberType: string } | null;
  overdueTasks: number;
  upcomingHearings: number;
  healthFlag: 'stalled' | 'at_risk' | 'inactive';
}

export async function fetchMatterHealth(inactiveDays = 21): Promise<MatterHealthItem[]> {
  const res = await api.get('/reports/matter-health', { params: { inactiveDays } });
  return (res.data as any).data ?? [];
}

// ─── Clerk Operations ───────────────────────────────────────

export interface ClerkOpsHearing {
  id: string;
  hearingDate: string;
  hearingType: string;
  status: string;
  court: { shortName: string } | null;
  case: {
    id: string;
    caseName: string;
    caseNumber: string;
    leadBarrister: { firstName: string; lastName: string; memberType: string } | null;
  };
}

export interface ClerkOperationsData {
  pendingAllocations: number;
  unassignedMatters: number;
  pendingConferences: number;
  upcomingHearings: ClerkOpsHearing[];
}

export async function fetchClerkOperations(): Promise<ClerkOperationsData> {
  const res = await api.get('/reports/clerk-operations');
  const d = (res.data as any).data;
  return {
    pendingAllocations: d?.pendingAllocations ?? 0,
    unassignedMatters: d?.unassignedMatters ?? 0,
    pendingConferences: d?.pendingConferences ?? 0,
    upcomingHearings: d?.upcomingHearings ?? [],
  };
}

// ─── Financial Performance ──────────────────────────────────

export interface FinancialPerformanceData {
  summary: {
    totalBilled: number;
    totalCollected: number;
    totalOutstanding: number;
    collectionRate: number;
    feeNoteCount: number;
  };
  monthly: Array<{ month: string; billed: number; collected: number }>;
  byPracticeArea: Array<{ practiceArea: string; billed: number; collected: number; count: number }>;
}

export async function fetchFinancialPerformance(from: string, to: string): Promise<FinancialPerformanceData> {
  const res = await api.get('/reports/financial-performance', { params: { from, to } });
  const d = (res.data as any).data;
  return {
    summary: d?.summary ?? { totalBilled: 0, totalCollected: 0, totalOutstanding: 0, collectionRate: 0, feeNoteCount: 0 },
    monthly: d?.monthly ?? [],
    byPracticeArea: d?.byPracticeArea ?? [],
  };
}

// ─── Outstanding Fees ───────────────────────────────────────

export interface OutstandingFeeRow {
  contactId: string | null;
  clientName: string;
  mattersCount: number;
  current: number;
  d30: number;
  d60: number;
  d90: number;
  total: number;
}

export async function fetchOutstandingFees(): Promise<OutstandingFeeRow[]> {
  const res = await api.get('/reports/outstanding-fees');
  const d = (res.data as any).data;
  // service returns { scope, rows } — extract the rows array
  return d?.rows ?? (Array.isArray(d) ? d : []);
}

// ─── Operational Alerts ─────────────────────────────────────

export interface OperationalAlert {
  level: 'critical' | 'warning' | 'info';
  key: string;
  message: string;
  count: number;
}

export async function fetchOperationalAlerts(): Promise<OperationalAlert[]> {
  const res = await api.get('/reports/alerts');
  return (res.data as any).data ?? [];
}

// ─── Revenue by Matter ──────────────────────────────────────

export interface RevenueByMatterRow {
  caseId: string;
  caseNumber: string;
  caseName: string;
  practiceArea: string | null;
  leadBarrister: string | null;
  billed: number;
  collected: number;
  outstanding: number;
  collectionRate: number;
  feeNoteCount: number;
}

export async function fetchRevenueByMatter(from: string, to: string): Promise<RevenueByMatterRow[]> {
  const res = await api.get('/reports/revenue-by-matter', { params: { from, to } });
  return (res.data as any).data ?? [];
}

// ─── Revenue by Barrister ───────────────────────────────────

export interface RevenueByBarristerRow {
  barristerId: string;
  firstName: string;
  lastName: string;
  memberType: string;
  billed: number;
  collected: number;
  outstanding: number;
  collectionRate: number;
  feeNoteCount: number;
  matterCount: number;
}

export async function fetchRevenueByBarrister(from: string, to: string): Promise<RevenueByBarristerRow[]> {
  const res = await api.get('/reports/revenue-by-barrister', { params: { from, to } });
  return (res.data as any).data ?? [];
}

// ─── CSV export utility ─────────────────────────────────────

export function downloadCSV(filename: string, rows: Record<string, string | number | boolean | null | undefined>[]) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]!);
  const csv = [
    headers.join(','),
    ...rows.map((r) =>
      headers.map((h) => {
        const v = r[h];
        if (v === null || v === undefined) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')
    ),
  ].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
